import { Schema } from "effect";
import {
  OfficeBoundaryParseOptions,
  OfficeCanonicalIdSchema,
  OfficeDocumentAlignmentSchema,
  OfficeDocumentFontSizePtSchema,
  OfficeDocumentHighlightSchema,
  OfficeDocumentInlineMarkSchema,
  OfficeDocumentTextColorSchema,
  OfficeNativeIdentityAliasSchema,
  OfficePublicErrorSchema,
  OfficeSpreadsheetHorizontalAlignmentSchema,
  OfficeSpreadsheetNumberFormatSchema,
  OfficeSpreadsheetSheetNameSchema,
} from "./office";
import {
  OfficeArtifactIdSchema,
  OfficeArtifactRevisionIdSchema,
  OfficeStorageObjectIdSchema,
} from "./office-content";
import {
  ProjectFileActorUserIdSchema,
  ProjectFileContentTypeSchema,
  ProjectFileEtagSchema,
  ProjectFileIdSchema,
  ProjectFileNameSchema,
  ProjectFilePositiveIntegerSchema,
  ProjectFilePrivateObjectKeySchema,
  ProjectFileRevisionIdSchema,
  ProjectFileSha256Schema,
  ProjectFileStorageObjectIdSchema,
} from "./project-files";
import {
  ArtifactRetentionSchema,
  SandboxProjectIdSchema,
  SandboxRequestIdSchema,
  SandboxWorkspaceIdSchema,
} from "./sandbox";
import { NonNegativeSafeIntegerSchema, toPapersFlowStandardSchema } from "./standard";
import { utf8ByteLength } from "./utf8";

export const OFFICE_CREATION_MAX_BYTES = 50 * 1_048_576;
export const OFFICE_CREATION_MAX_DOCUMENT_BLOCKS = 48;
export const OFFICE_CREATION_MAX_DOCUMENT_CHARACTERS = 100_000;
export const OFFICE_CREATION_MAX_PRESENTATION_SLIDES = 20;
export const OFFICE_CREATION_MAX_PRESENTATION_CHARACTERS = 100_000;
export const OFFICE_CREATION_MAX_WORKBOOK_SHEETS = 16;
export const OFFICE_CREATION_MAX_WORKBOOK_ROWS = 256;
export const OFFICE_CREATION_MAX_WORKBOOK_COLUMNS = 64;
export const OFFICE_CREATION_MAX_WORKBOOK_CELLS = 16_384;
export const OFFICE_CREATION_MAX_WORKBOOK_CHARACTERS = 100_000;
export const OFFICE_CREATION_MAX_ENGINE_OPERATIONS = 512;
export const OFFICE_CREATION_MAX_COMPATIBILITY_ISSUES = 16;
export const OFFICE_IMPORT_MAX_COMPATIBILITY_ISSUES = 9;
export const OFFICE_PDF_TO_DOCX_MAX_PAGES = 2_000;
export const OFFICE_PDF_TO_DOCX_MAX_PAGE_DIMENSION_POINTS = 4_096;
export const OFFICE_PDF_TO_DOCX_MAX_PAGE_RENDER_PIXELS = 33_554_432;
export const OFFICE_PDF_TO_DOCX_MAX_TOTAL_RENDER_PIXELS = 268_435_456;
export const OFFICE_PDF_TO_DOCX_MAX_REPORTED_WARNINGS = 256;
export const OFFICE_PDF_TO_DOCX_MAX_WARNING_CHARACTERS = 500;
export const OFFICE_PDF_TO_DOCX_RENDER_SCALE = 2;
export const OFFICE_CREATION_OPERATION_ID_MAX_LENGTH = 335;
export const OFFICE_CREATION_MAX_REQUEST_BYTES = 512 * 1_024;
export const OFFICE_CREATION_INTERNAL_MAX_BYTES = OFFICE_CREATION_MAX_REQUEST_BYTES + 16 * 1_024;
export const OFFICE_CREATION_LEASE_MS = 30_000;
export const OFFICE_CREATION_HEARTBEAT_MS = 5_000;
export const OFFICE_CREATION_WAIT_TIMEOUT_MS = 5 * 60_000;

const XmlCompatibleTextSchema = Schema.String.check(
  Schema.isMaxLength(32_000),
  Schema.makeFilter(
    (value) =>
      Array.from(value).every((character) => {
        const codePoint = character.codePointAt(0);
        return (
          codePoint === 0x09 ||
          codePoint === 0x0a ||
          codePoint === 0x0d ||
          (codePoint !== undefined &&
            ((codePoint >= 0x20 && codePoint <= 0xd7ff) ||
              (codePoint >= 0xe000 && codePoint <= 0xfffd) ||
              (codePoint >= 0x10000 && codePoint <= 0x10ffff)))
        );
      }) || "must contain only XML-compatible text",
  ),
);
const XmlTextSchema = XmlCompatibleTextSchema.check(Schema.isMinLength(1));

const TitleSchema = Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(160));
const SpeakerNotesSchema = XmlTextSchema.check(Schema.isMaxLength(8_000));
const DocumentBlockTextSchema = XmlTextSchema.check(Schema.isMaxLength(10_000));
const PresentationTitleSchema = XmlTextSchema.check(Schema.isMaxLength(500));
const PresentationBodyTextSchema = XmlTextSchema.check(Schema.isMaxLength(2_000));

const DocumentCreationFormatSchema = Schema.Struct({
  marks: Schema.optionalKey(
    Schema.Array(OfficeDocumentInlineMarkSchema)
      .check(Schema.isMinLength(1), Schema.isMaxLength(4))
      .check(
        Schema.makeFilter(
          (marks) => new Set(marks).size === marks.length || "document marks must be unique",
        ),
      ),
  ),
  fontSizePt: Schema.optionalKey(OfficeDocumentFontSizePtSchema),
  textColor: Schema.optionalKey(OfficeDocumentTextColorSchema),
  highlight: Schema.optionalKey(OfficeDocumentHighlightSchema),
}).check(
  Schema.makeFilter(
    (format) =>
      format.marks !== undefined ||
      format.fontSizePt !== undefined ||
      format.textColor !== undefined ||
      format.highlight !== undefined ||
      "document format must set at least one supported property",
  ),
);

const DocumentTextBlockFields = {
  text: DocumentBlockTextSchema,
  alignment: Schema.optionalKey(OfficeDocumentAlignmentSchema),
  format: Schema.optionalKey(DocumentCreationFormatSchema),
} as const;

export const OfficeDocumentCreationBlockSchema = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("paragraph"),
    ...DocumentTextBlockFields,
  }),
  Schema.Struct({
    type: Schema.Literal("heading"),
    level: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 6 })),
    ...DocumentTextBlockFields,
  }),
  Schema.Struct({
    type: Schema.Literal("list-item"),
    listKind: Schema.Literals(["bullet", "ordered"]),
    level: Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 4 })),
    ...DocumentTextBlockFields,
  }),
  Schema.Struct({ type: Schema.Literal("page-break") }),
]);

export const OfficeDocumentCreationSpecSchema = Schema.Struct({
  title: TitleSchema,
  blocks: Schema.Array(OfficeDocumentCreationBlockSchema)
    .check(Schema.isMinLength(1), Schema.isMaxLength(OFFICE_CREATION_MAX_DOCUMENT_BLOCKS))
    .check(
      Schema.makeFilter(
        (blocks) =>
          blocks.reduce((total, block) => total + ("text" in block ? block.text.length : 0), 0) <=
            OFFICE_CREATION_MAX_DOCUMENT_CHARACTERS ||
          "document creation text exceeds the total character limit",
      ),
    ),
});

const PresentationSlideFields = {
  title: PresentationTitleSchema,
  speakerNotes: Schema.optionalKey(SpeakerNotesSchema),
} as const;

export const OfficePresentationCreationSlideSchema = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("title"),
    ...PresentationSlideFields,
    subtitle: Schema.optionalKey(PresentationBodyTextSchema),
  }),
  Schema.Struct({
    type: Schema.Literal("content"),
    ...PresentationSlideFields,
    bullets: Schema.Array(PresentationBodyTextSchema).check(
      Schema.isMinLength(1),
      Schema.isMaxLength(12),
    ),
  }),
]);

export const OfficePresentationCreationSpecSchema = Schema.Struct({
  title: TitleSchema,
  slides: Schema.Array(OfficePresentationCreationSlideSchema)
    .check(Schema.isMinLength(1), Schema.isMaxLength(OFFICE_CREATION_MAX_PRESENTATION_SLIDES))
    .check(
      Schema.makeFilter(
        (slides) =>
          slides.reduce(
            (total, slide) =>
              total +
              slide.title.length +
              (slide.speakerNotes?.length ?? 0) +
              (slide.type === "title"
                ? (slide.subtitle?.length ?? 0)
                : slide.bullets.reduce((subtotal, bullet) => subtotal + bullet.length, 0)),
            0,
          ) <= OFFICE_CREATION_MAX_PRESENTATION_CHARACTERS ||
          "presentation creation text exceeds the total character limit",
      ),
    ),
});

const WorkbookFormulaSchema = XmlTextSchema.check(
  Schema.isMaxLength(8_192),
  Schema.makeFilter((formula) => formula.startsWith("=") || "workbook formulas must start with ="),
  Schema.makeFilter(
    (formula) =>
      (!formula.includes("[") &&
        !formula.includes("]") &&
        !formula.includes("|") &&
        !/(?:WEBSERVICE|HYPERLINK|RTD|CALL|REGISTER\.ID|_xll\.|IMPORT[A-Z]*)\s*\(/iu.test(
          formula,
        ) &&
        !/\b(?:https?|ftp):/iu.test(formula)) ||
      "workbook formulas must not contain external-data or executable constructs",
  ),
);

export const OfficeWorkbookCellSchema = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("value"),
    value: Schema.Union([XmlCompatibleTextSchema, Schema.Finite, Schema.Boolean, Schema.Null]),
  }),
  Schema.Struct({ type: Schema.Literal("formula"), formula: WorkbookFormulaSchema }),
]);

function workbookCellCharacterCount(cell: typeof OfficeWorkbookCellSchema.Type): number {
  if (cell.type === "formula") return cell.formula.length;
  return typeof cell.value === "string" ? cell.value.length : 0;
}

export const OfficeWorkbookColumnFormatSchema = Schema.Struct({
  columnIndex: Schema.Int.check(
    Schema.isBetween({ minimum: 0, maximum: OFFICE_CREATION_MAX_WORKBOOK_COLUMNS - 1 }),
  ),
  bold: Schema.optionalKey(Schema.Boolean),
  italic: Schema.optionalKey(Schema.Boolean),
  horizontalAlignment: Schema.optionalKey(OfficeSpreadsheetHorizontalAlignmentSchema),
  numberFormat: Schema.optionalKey(OfficeSpreadsheetNumberFormatSchema),
}).check(
  Schema.makeFilter(
    (format) =>
      format.bold !== undefined ||
      format.italic !== undefined ||
      format.horizontalAlignment !== undefined ||
      format.numberFormat !== undefined ||
      "workbook column format must set at least one supported property",
  ),
);

const WorkbookRowsSchema = Schema.Array(
  Schema.Array(OfficeWorkbookCellSchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(OFFICE_CREATION_MAX_WORKBOOK_COLUMNS),
  ),
)
  .check(Schema.isMinLength(1), Schema.isMaxLength(OFFICE_CREATION_MAX_WORKBOOK_ROWS))
  .check(
    Schema.makeFilter(
      (rows) =>
        rows.every((row) => row.length === rows[0]?.length) ||
        "workbook sheet rows must form one rectangular range",
    ),
  );

export const OfficeWorkbookCreationSheetSchema = Schema.Struct({
  name: OfficeSpreadsheetSheetNameSchema,
  headerRow: Schema.Boolean,
  rows: WorkbookRowsSchema,
  columnFormats: Schema.Array(OfficeWorkbookColumnFormatSchema)
    .check(Schema.isMaxLength(OFFICE_CREATION_MAX_WORKBOOK_COLUMNS))
    .check(
      Schema.makeFilter(
        (formats) =>
          new Set(formats.map(({ columnIndex }) => columnIndex)).size === formats.length ||
          "workbook column formats must target unique columns",
      ),
    ),
}).check(
  Schema.makeFilter(
    (sheet) =>
      sheet.columnFormats.every(({ columnIndex }) => columnIndex < (sheet.rows[0]?.length ?? 0)) ||
      "workbook column formats must target a populated column",
  ),
);

export const OfficeWorkbookCreationSpecSchema = Schema.Struct({
  title: TitleSchema,
  sheets: Schema.Array(OfficeWorkbookCreationSheetSchema)
    .check(Schema.isMinLength(1), Schema.isMaxLength(OFFICE_CREATION_MAX_WORKBOOK_SHEETS))
    .check(
      Schema.makeFilter(
        (sheets) =>
          new Set(sheets.map(({ name }) => name.toLocaleLowerCase("en-US"))).size ===
            sheets.length || "workbook sheet names must be unique",
      ),
      Schema.makeFilter(
        (sheets) =>
          sheets.reduce(
            (total, sheet) => total + sheet.rows.length * (sheet.rows[0]?.length ?? 0),
            0,
          ) <= OFFICE_CREATION_MAX_WORKBOOK_CELLS ||
          "workbook creation exceeds the total cell limit",
      ),
      Schema.makeFilter(
        (sheets) =>
          sheets.reduce(
            (total, sheet) =>
              total +
              sheet.rows.reduce(
                (sheetTotal, row) =>
                  sheetTotal +
                  row.reduce((rowTotal, cell) => rowTotal + workbookCellCharacterCount(cell), 0),
                0,
              ),
            0,
          ) <= OFFICE_CREATION_MAX_WORKBOOK_CHARACTERS ||
          "workbook creation text exceeds the total character limit",
      ),
    ),
});

export const OfficeCreationSpecSchema = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("document"), spec: OfficeDocumentCreationSpecSchema }),
  Schema.Struct({
    kind: Schema.Literal("presentation"),
    spec: OfficePresentationCreationSpecSchema,
  }),
  Schema.Struct({ kind: Schema.Literal("spreadsheet"), spec: OfficeWorkbookCreationSpecSchema }),
]);

const CompatibilityIssueCountSchema = ProjectFilePositiveIntegerSchema;

export const OfficeCompatibilityIssueSchema = Schema.Union([
  Schema.Struct({
    code: Schema.Literal("content-preserved-read-only"),
    count: CompatibilityIssueCountSchema,
    handling: Schema.Literal("preserved-read-only"),
  }),
  Schema.Struct({
    code: Schema.Literal("formatting-preserved-unedited"),
    count: CompatibilityIssueCountSchema,
    handling: Schema.Literal("preserved-unedited"),
  }),
  Schema.Struct({
    code: Schema.Literal("hidden-content-preserved"),
    count: CompatibilityIssueCountSchema,
    handling: Schema.Literal("preserved-unedited"),
  }),
  Schema.Struct({
    code: Schema.Literal("rendering-approximated"),
    count: CompatibilityIssueCountSchema,
    handling: Schema.Literal("rendered-approximately"),
  }),
  Schema.Struct({
    code: Schema.Literal("calculation-refresh-required"),
    count: CompatibilityIssueCountSchema,
    handling: Schema.Literal("recalculated-on-open"),
  }),
]);

export const OfficeCompatibilityReportSchema = Schema.Struct({
  disposition: Schema.Literals(["editable", "editable-with-warnings"]),
  issues: Schema.Array(OfficeCompatibilityIssueSchema)
    .check(Schema.isMaxLength(OFFICE_CREATION_MAX_COMPATIBILITY_ISSUES))
    .check(
      Schema.makeFilter(
        (issues) =>
          new Set(issues.map(({ code }) => code)).size === issues.length ||
          "Office compatibility issue codes must be unique",
      ),
    ),
}).check(
  Schema.makeFilter(
    (report) =>
      (report.disposition === "editable" && report.issues.length === 0) ||
      (report.disposition === "editable-with-warnings" && report.issues.length > 0) ||
      "Office compatibility disposition must match its issues",
  ),
);

export const OfficeImportCompatibilityIssueSchema = Schema.Union([
  OfficeCompatibilityIssueSchema,
  Schema.Struct({
    code: Schema.Literal("unsupported-formulas"),
    count: ProjectFilePositiveIntegerSchema,
    handling: Schema.Literal("preserved-unrecalculated"),
  }),
  Schema.Struct({
    code: Schema.Literal("unsupported-styles"),
    count: ProjectFilePositiveIntegerSchema,
    handling: Schema.Literal("preserved-unedited"),
  }),
  Schema.Struct({
    code: Schema.Literal("unsupported-charts"),
    count: ProjectFilePositiveIntegerSchema,
    handling: Schema.Literal("preserved-unedited"),
  }),
  Schema.Struct({
    code: Schema.Literal("inert-external-links"),
    count: ProjectFilePositiveIntegerSchema,
    handling: Schema.Literal("preserved-inert"),
  }),
]);

export const OfficeImportCompatibilityReportSchema = Schema.Struct({
  disposition: Schema.Literals(["editable", "editable-with-warnings"]),
  issues: Schema.Array(OfficeImportCompatibilityIssueSchema)
    .check(Schema.isMaxLength(OFFICE_IMPORT_MAX_COMPATIBILITY_ISSUES))
    .check(
      Schema.makeFilter(
        (issues) =>
          new Set(issues.map(({ code }) => code)).size === issues.length ||
          "Office import compatibility issue codes must be unique",
      ),
    ),
}).check(
  Schema.makeFilter(
    (report) =>
      (report.disposition === "editable" && report.issues.length === 0) ||
      (report.disposition === "editable-with-warnings" && report.issues.length > 0) ||
      "Office import compatibility disposition must match its issues",
  ),
);

export const OfficePdfToDocxConversionMetadataSchema = Schema.Struct({
  converter: Schema.Literal("genoffice-pdf2docx"),
  converterVersion: OfficeCanonicalIdSchema.check(Schema.isMaxLength(120)),
  pageCount: ProjectFilePositiveIntegerSchema.check(
    Schema.isLessThanOrEqualTo(OFFICE_PDF_TO_DOCX_MAX_PAGES),
  ),
  scannedDocument: Schema.Boolean,
  warningCount: NonNegativeSafeIntegerSchema,
  warnings: Schema.Array(
    Schema.String.check(
      Schema.isMinLength(1),
      Schema.isMaxLength(OFFICE_PDF_TO_DOCX_MAX_WARNING_CHARACTERS),
    ),
  ).check(Schema.isMaxLength(OFFICE_PDF_TO_DOCX_MAX_REPORTED_WARNINGS)),
  warningsTruncated: Schema.Boolean,
}).check(
  Schema.makeFilter(
    (metadata) =>
      metadata.warningCount >= metadata.warnings.length ||
      "PDF-to-DOCX warningCount must include every reported warning",
  ),
  Schema.makeFilter(
    (metadata) =>
      metadata.warningsTruncated === metadata.warningCount > metadata.warnings.length ||
      "PDF-to-DOCX warning truncation must match the reported warning count",
  ),
);

export const OfficeCreationIdSchema = OfficeCanonicalIdSchema.pipe(
  Schema.brand("OfficeCreationId"),
);
export const OfficeCreationOperationIdSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(OFFICE_CREATION_OPERATION_ID_MAX_LENGTH),
  Schema.makeFilter(
    (value) =>
      value === value.trim() &&
      Array.from(value).every((character) => {
        const codePoint = character.codePointAt(0);
        return codePoint !== undefined && codePoint > 31 && codePoint !== 127;
      }),
    { expected: "a canonical Office creation operation identifier" },
  ),
).pipe(Schema.brand("OfficeCreationOperationId"));
export const OfficeCreationWorkerIdSchema = OfficeCanonicalIdSchema.pipe(
  Schema.brand("OfficeCreationWorkerId"),
);

const OfficeCreationRequestByteSizeFilter = Schema.makeFilter(
  (request) =>
    utf8ByteLength(JSON.stringify(request)) <= OFFICE_CREATION_MAX_REQUEST_BYTES ||
    "Office creation request exceeds the UTF-8 JSON transport limit",
);

const OfficeCreationScopeFields = {
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  threadId: OfficeCanonicalIdSchema,
  runId: OfficeCanonicalIdSchema,
  attemptId: OfficeCanonicalIdSchema,
  attemptGeneration: NonNegativeSafeIntegerSchema.check(Schema.isGreaterThanOrEqualTo(1)),
  operationId: OfficeCreationOperationIdSchema,
} as const;

export const OfficeRepositoryBeginCreationRequestSchema = Schema.Struct({
  ...OfficeCreationScopeFields,
  actorUserId: ProjectFileActorUserIdSchema,
  toolCallId: OfficeCanonicalIdSchema,
  creation: OfficeCreationSpecSchema,
}).check(
  Schema.makeFilter(
    (request) =>
      request.operationId === `office-create:${request.attemptId}:${request.toolCallId}` ||
      "Office creation operation identity must match its attempt and tool call",
  ),
  OfficeCreationRequestByteSizeFilter,
);

const OfficeProjectFilePromotionFields = {
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  threadId: OfficeCanonicalIdSchema,
  fileId: ProjectFileIdSchema,
  fileRevisionId: ProjectFileRevisionIdSchema,
} as const;

export const OfficeProjectFilePromotionRequestSchema = Schema.Struct(
  OfficeProjectFilePromotionFields,
);
export const OfficeRepositoryBeginProjectFileImportRequestSchema = Schema.Struct({
  ...OfficeProjectFilePromotionFields,
  actorUserId: ProjectFileActorUserIdSchema,
});

const OfficePdfToDocxRequestFields = {
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  threadId: OfficeCanonicalIdSchema,
  sourceArtifactId: OfficeArtifactIdSchema,
  sourceArtifactRevisionId: OfficeArtifactRevisionIdSchema,
} as const;

export const OfficePdfToDocxRequestSchema = Schema.Struct(OfficePdfToDocxRequestFields);
export const OfficeRepositoryBeginPdfToDocxRequestSchema = Schema.Struct({
  ...OfficePdfToDocxRequestFields,
  actorUserId: ProjectFileActorUserIdSchema,
});

const ArtifactReferenceFields = {
  version: Schema.Literal("office-created-artifact/v1"),
  operationId: OfficeCreationOperationIdSchema,
  artifactId: OfficeArtifactIdSchema,
  artifactRevisionId: OfficeArtifactRevisionIdSchema,
  revision: ProjectFilePositiveIntegerSchema,
  status: Schema.Literal("ready"),
} as const;

const PromptCreatedArtifactReferenceFields = {
  ...ArtifactReferenceFields,
  origin: Schema.Literal("prompt"),
  title: TitleSchema,
  compatibility: OfficeCompatibilityReportSchema,
} as const;

export const OfficePromptCreatedArtifactReferenceSchema = Schema.Union([
  Schema.Struct({
    ...PromptCreatedArtifactReferenceFields,
    kind: Schema.Literal("document"),
    mediaType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ),
  }),
  Schema.Struct({
    ...PromptCreatedArtifactReferenceFields,
    kind: Schema.Literal("presentation"),
    mediaType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ),
  }),
  Schema.Struct({
    ...PromptCreatedArtifactReferenceFields,
    kind: Schema.Literal("spreadsheet"),
    mediaType: Schema.Literal("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"),
  }),
]);

const ImportedArtifactReferenceFields = {
  ...ArtifactReferenceFields,
  origin: Schema.Literal("project-file-import"),
  title: ProjectFileNameSchema,
  compatibility: OfficeImportCompatibilityReportSchema,
} as const;

export const OfficeImportedArtifactReferenceSchema = Schema.Union([
  Schema.Struct({
    ...ImportedArtifactReferenceFields,
    kind: Schema.Literal("document"),
    mediaType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ),
  }),
  Schema.Struct({
    ...ImportedArtifactReferenceFields,
    kind: Schema.Literal("presentation"),
    mediaType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ),
  }),
  Schema.Struct({
    ...ImportedArtifactReferenceFields,
    kind: Schema.Literal("spreadsheet"),
    mediaType: Schema.Literal("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"),
  }),
  Schema.Struct({
    ...ImportedArtifactReferenceFields,
    kind: Schema.Literal("pdf"),
    mediaType: Schema.Literal("application/pdf"),
  }),
  Schema.Struct({
    ...ImportedArtifactReferenceFields,
    kind: Schema.Literal("markdown"),
    mediaType: Schema.Literal("text/markdown"),
  }),
]);

export const OfficePdfToDocxArtifactReferenceSchema = Schema.Struct({
  ...ArtifactReferenceFields,
  origin: Schema.Literal("pdf-to-docx"),
  sourceArtifactId: OfficeArtifactIdSchema,
  sourceArtifactRevisionId: OfficeArtifactRevisionIdSchema,
  title: TitleSchema,
  kind: Schema.Literal("document"),
  mediaType: Schema.Literal(
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ),
  compatibility: OfficeCompatibilityReportSchema,
  conversion: OfficePdfToDocxConversionMetadataSchema,
});

export const OfficeCreatedArtifactReferenceSchema = Schema.Union([
  OfficePromptCreatedArtifactReferenceSchema,
  OfficeImportedArtifactReferenceSchema,
  OfficePdfToDocxArtifactReferenceSchema,
]);

export const OfficeRepositoryBeginCreationResultSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("pending"),
    creationId: OfficeCreationIdSchema,
    cancellationGeneration: NonNegativeSafeIntegerSchema,
    operationId: OfficeCreationOperationIdSchema,
    artifactId: OfficeArtifactIdSchema,
    artifactRevisionId: OfficeArtifactRevisionIdSchema,
    revision: ProjectFilePositiveIntegerSchema,
  }),
  Schema.Struct({
    status: Schema.Literal("ready"),
    creationId: OfficeCreationIdSchema,
    result: OfficePromptCreatedArtifactReferenceSchema,
  }),
]);

export const OfficeRepositoryBeginProjectFileImportResultSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("pending"),
    creationId: OfficeCreationIdSchema,
    cancellationGeneration: NonNegativeSafeIntegerSchema,
    operationId: OfficeCreationOperationIdSchema,
    artifactId: OfficeArtifactIdSchema,
    artifactRevisionId: OfficeArtifactRevisionIdSchema,
    revision: ProjectFilePositiveIntegerSchema,
  }),
  Schema.Struct({
    status: Schema.Literal("ready"),
    creationId: OfficeCreationIdSchema,
    result: OfficeImportedArtifactReferenceSchema,
  }),
]);

export const OfficeRepositoryBeginPdfToDocxResultSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("pending"),
    creationId: OfficeCreationIdSchema,
    cancellationGeneration: NonNegativeSafeIntegerSchema,
    operationId: OfficeCreationOperationIdSchema,
    artifactId: OfficeArtifactIdSchema,
    artifactRevisionId: OfficeArtifactRevisionIdSchema,
    revision: ProjectFilePositiveIntegerSchema,
  }),
  Schema.Struct({
    status: Schema.Literal("ready"),
    creationId: OfficeCreationIdSchema,
    result: OfficePdfToDocxArtifactReferenceSchema,
  }),
]);

const OfficeCreationRepositoryScopeFields = {
  ...OfficeCreationScopeFields,
  actorUserId: ProjectFileActorUserIdSchema,
  creationId: OfficeCreationIdSchema,
} as const;

export const OfficeRepositoryGetCreationRequestSchema = Schema.Struct(
  OfficeCreationRepositoryScopeFields,
);
export const OfficeRepositoryCancelCreationRequestSchema = Schema.Struct(
  OfficeCreationRepositoryScopeFields,
);

const OfficeRepositoryProjectFileImportActorFields = {
  ...OfficeProjectFilePromotionFields,
  actorUserId: ProjectFileActorUserIdSchema,
  creationId: OfficeCreationIdSchema,
} as const;

export const OfficeRepositoryGetProjectFileImportRequestSchema = Schema.Struct(
  OfficeRepositoryProjectFileImportActorFields,
);
export const OfficeRepositoryCancelProjectFileImportRequestSchema = Schema.Struct(
  OfficeRepositoryProjectFileImportActorFields,
);

const OfficeRepositoryPdfToDocxActorFields = {
  ...OfficePdfToDocxRequestFields,
  actorUserId: ProjectFileActorUserIdSchema,
  creationId: OfficeCreationIdSchema,
} as const;

export const OfficeRepositoryGetPdfToDocxRequestSchema = Schema.Struct(
  OfficeRepositoryPdfToDocxActorFields,
);
export const OfficeRepositoryCancelPdfToDocxRequestSchema = Schema.Struct(
  OfficeRepositoryPdfToDocxActorFields,
);

export const OfficeRepositoryCreationStatusSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literals(["pending", "processing"]),
    operationId: OfficeCreationOperationIdSchema,
    artifactId: OfficeArtifactIdSchema,
    artifactRevisionId: OfficeArtifactRevisionIdSchema,
    revision: ProjectFilePositiveIntegerSchema,
  }),
  Schema.Struct({
    status: Schema.Literal("ready"),
    result: OfficePromptCreatedArtifactReferenceSchema,
  }),
  Schema.Struct({
    status: Schema.Literals(["failed", "cancelled"]),
    operationId: OfficeCreationOperationIdSchema,
    error: OfficePublicErrorSchema,
  }),
]);

export const OfficeRepositoryProjectFileImportStatusSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literals(["pending", "processing"]),
    operationId: OfficeCreationOperationIdSchema,
    artifactId: OfficeArtifactIdSchema,
    artifactRevisionId: OfficeArtifactRevisionIdSchema,
    revision: ProjectFilePositiveIntegerSchema,
  }),
  Schema.Struct({
    status: Schema.Literal("ready"),
    result: OfficeImportedArtifactReferenceSchema,
  }),
  Schema.Struct({
    status: Schema.Literals(["failed", "cancelled"]),
    operationId: OfficeCreationOperationIdSchema,
    error: OfficePublicErrorSchema,
  }),
]);

export const OfficeRepositoryPdfToDocxStatusSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literals(["pending", "processing"]),
    operationId: OfficeCreationOperationIdSchema,
    artifactId: OfficeArtifactIdSchema,
    artifactRevisionId: OfficeArtifactRevisionIdSchema,
    revision: ProjectFilePositiveIntegerSchema,
  }),
  Schema.Struct({
    status: Schema.Literal("ready"),
    result: OfficePdfToDocxArtifactReferenceSchema,
  }),
  Schema.Struct({
    status: Schema.Literals(["failed", "cancelled"]),
    operationId: OfficeCreationOperationIdSchema,
    error: OfficePublicErrorSchema,
  }),
]);

export const OfficeRepositoryCancelCreationResultSchema = Schema.Struct({
  status: Schema.Literals(["ready", "failed", "cancelled"]),
});
export const OfficeRepositoryCancelProjectFileImportResultSchema =
  OfficeRepositoryCancelCreationResultSchema;
export const OfficeRepositoryCancelPdfToDocxResultSchema =
  OfficeRepositoryCancelCreationResultSchema;

export const OfficeCreationPointerSchema = Schema.Struct({
  schemaVersion: Schema.Literal(1),
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  creationId: OfficeCreationIdSchema,
  cancellationGeneration: NonNegativeSafeIntegerSchema,
});

export const OfficeCreationStartResultSchema = Schema.Struct({
  accepted: Schema.Literal(true),
  creationId: OfficeCreationIdSchema,
});

const OfficeWorkerCreationScopeFields = {
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  creationId: OfficeCreationIdSchema,
} as const;

export const OfficeClaimCreationRequestSchema = Schema.Struct({
  ...OfficeWorkerCreationScopeFields,
  workerId: OfficeCreationWorkerIdSchema,
  leaseDurationMs: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(5_000),
    Schema.isLessThanOrEqualTo(120_000),
  ),
});

export const OfficeCreationLeaseSchema = Schema.Struct({
  ...OfficeWorkerCreationScopeFields,
  workerId: OfficeCreationWorkerIdSchema,
  leaseGeneration: ProjectFilePositiveIntegerSchema,
  cancellationGeneration: NonNegativeSafeIntegerSchema,
});

const ClaimedCreationFields = {
  lease: OfficeCreationLeaseSchema,
  operationId: OfficeCreationOperationIdSchema,
  artifactId: OfficeArtifactIdSchema,
  artifactRevisionId: OfficeArtifactRevisionIdSchema,
  revision: ProjectFilePositiveIntegerSchema,
} as const;
const ClaimedCreationOutputFields = {
  revisionId: OfficeArtifactRevisionIdSchema,
  storageObjectId: OfficeStorageObjectIdSchema,
  objectKey: ProjectFilePrivateObjectKeySchema,
  maxByteSize: Schema.Literal(OFFICE_CREATION_MAX_BYTES),
} as const;

export const OfficeClaimedPromptCreationSchema = Schema.Union([
  Schema.Struct({
    ...ClaimedCreationFields,
    origin: Schema.Literal("prompt"),
    creation: Schema.Struct({
      kind: Schema.Literal("document"),
      spec: OfficeDocumentCreationSpecSchema,
    }),
    output: Schema.Struct({
      ...ClaimedCreationOutputFields,
      contentType: Schema.Literal(
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ),
    }),
  }),
  Schema.Struct({
    ...ClaimedCreationFields,
    origin: Schema.Literal("prompt"),
    creation: Schema.Struct({
      kind: Schema.Literal("presentation"),
      spec: OfficePresentationCreationSpecSchema,
    }),
    output: Schema.Struct({
      ...ClaimedCreationOutputFields,
      contentType: Schema.Literal(
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      ),
    }),
  }),
  Schema.Struct({
    ...ClaimedCreationFields,
    origin: Schema.Literal("prompt"),
    creation: Schema.Struct({
      kind: Schema.Literal("spreadsheet"),
      spec: OfficeWorkbookCreationSpecSchema,
    }),
    output: Schema.Struct({
      ...ClaimedCreationOutputFields,
      contentType: Schema.Literal(
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ),
    }),
  }),
]).check(
  Schema.makeFilter(
    (claim) =>
      claim.artifactRevisionId === claim.output.revisionId ||
      "Office prompt output must use the reserved artifact revision",
  ),
);

const ProjectFileImportSourceFields = {
  fileId: ProjectFileIdSchema,
  fileRevisionId: ProjectFileRevisionIdSchema,
  revision: ProjectFilePositiveIntegerSchema,
  storageObjectId: ProjectFileStorageObjectIdSchema,
  retention: ArtifactRetentionSchema,
  objectKey: ProjectFilePrivateObjectKeySchema,
  fileName: ProjectFileNameSchema,
  storageContentType: ProjectFileContentTypeSchema,
  byteSize: ProjectFilePositiveIntegerSchema.check(
    Schema.isLessThanOrEqualTo(OFFICE_CREATION_MAX_BYTES),
  ),
  sha256: ProjectFileSha256Schema,
  etag: ProjectFileEtagSchema,
  storageStatus: Schema.Literal("verified"),
} as const;

export const OfficeProjectFileImportSourceSchema = Schema.Union([
  Schema.Struct({
    ...ProjectFileImportSourceFields,
    kind: Schema.Literal("document"),
    contentType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ),
  }),
  Schema.Struct({
    ...ProjectFileImportSourceFields,
    kind: Schema.Literal("presentation"),
    contentType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ),
  }),
  Schema.Struct({
    ...ProjectFileImportSourceFields,
    kind: Schema.Literal("spreadsheet"),
    contentType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ),
  }),
  Schema.Struct({
    ...ProjectFileImportSourceFields,
    kind: Schema.Literal("pdf"),
    contentType: Schema.Literal("application/pdf"),
  }),
  Schema.Struct({
    ...ProjectFileImportSourceFields,
    kind: Schema.Literal("markdown"),
    contentType: Schema.Literal("text/markdown"),
  }),
]).check(
  Schema.makeFilter((source) => {
    if (source.storageContentType === source.contentType) return true;
    const extension = source.fileName.toLowerCase().match(/\.([^.]+)$/u)?.[1];
    if (source.storageContentType === "text/plain") {
      return (
        (source.kind === "markdown" && (extension === "md" || extension === "markdown")) ||
        "Plain-text Office imports must use a Markdown file name"
      );
    }
    if (source.storageContentType !== "application/octet-stream") {
      return "Generic Office import media types must use a matching file extension";
    }
    const expectedExtension =
      source.kind === "document"
        ? "docx"
        : source.kind === "presentation"
          ? "pptx"
          : source.kind === "spreadsheet"
            ? "xlsx"
            : source.kind === "pdf"
              ? "pdf"
              : extension === "md" || extension === "markdown"
                ? extension
                : "md";
    return (
      extension === expectedExtension ||
      "Generic Office import media types must use a matching file extension"
    );
  }),
);

export const OfficeClaimedProjectFileImportSchema = Schema.Struct({
  ...ClaimedCreationFields,
  origin: Schema.Literal("project-file-import"),
  source: OfficeProjectFileImportSourceSchema,
}).check(
  Schema.makeFilter((claim) => {
    const prefix =
      `v1/${claim.source.retention}/workspaces/${claim.lease.workspaceId}` +
      `/projects/${claim.lease.projectId}/files/${claim.source.fileId}` +
      `/revisions/${claim.source.fileRevisionId}/`;
    const suffix = claim.source.objectKey.slice(prefix.length);
    return (
      (claim.source.objectKey.startsWith(prefix) && suffix !== "" && !suffix.includes("/")) ||
      "Office import source must use the exact project-file revision key"
    );
  }),
  Schema.makeFilter(
    (claim) =>
      claim.operationId === `project-file-import:${claim.source.fileRevisionId}` ||
      "Office import operation must be derived from the source file revision",
  ),
);

export const OfficePdfToDocxSourceStorageSchema = Schema.Union([
  Schema.Struct({ origin: Schema.Literal("artifact-revision") }),
  Schema.Struct({
    origin: Schema.Literal("project-file-revision"),
    sourceFileId: ProjectFileIdSchema,
    sourceFileRevisionId: ProjectFileRevisionIdSchema,
  }),
]);

export const OfficePdfToDocxStorageContentTypeSchema = Schema.Literals([
  "application/pdf",
  "application/octet-stream",
]);

export const OfficePdfToDocxSourceSchema = Schema.Struct({
  artifactId: OfficeArtifactIdSchema,
  artifactRevisionId: OfficeArtifactRevisionIdSchema,
  revision: ProjectFilePositiveIntegerSchema,
  storageObjectId: OfficeStorageObjectIdSchema,
  retention: ArtifactRetentionSchema,
  objectKey: ProjectFilePrivateObjectKeySchema,
  storage: OfficePdfToDocxSourceStorageSchema,
  kind: Schema.Literal("pdf"),
  contentType: Schema.Literal("application/pdf"),
  storageContentType: OfficePdfToDocxStorageContentTypeSchema,
  byteSize: ProjectFilePositiveIntegerSchema.check(
    Schema.isLessThanOrEqualTo(OFFICE_CREATION_MAX_BYTES),
  ),
  sha256: ProjectFileSha256Schema,
  etag: ProjectFileEtagSchema,
  storageStatus: Schema.Literal("verified"),
}).check(
  Schema.makeFilter(
    (source) =>
      source.storageContentType === source.contentType ||
      (source.storageContentType === "application/octet-stream" &&
        (source.storage.origin === "project-file-revision" ||
          source.objectKey.toLowerCase().endsWith(".pdf"))) ||
      "PDF-to-DOCX source storage media must be PDF or a proven generic-binary PDF",
  ),
);

export const OfficeClaimedPdfToDocxSchema = Schema.Struct({
  ...ClaimedCreationFields,
  origin: Schema.Literal("pdf-to-docx"),
  source: OfficePdfToDocxSourceSchema,
  output: Schema.Struct({
    ...ClaimedCreationOutputFields,
    contentType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ),
  }),
}).check(
  Schema.makeFilter(
    (claim) =>
      claim.operationId === `pdf-to-docx:${claim.source.artifactRevisionId}` ||
      "PDF-to-DOCX operation must be derived from the source artifact revision",
  ),
  Schema.makeFilter(
    (claim) =>
      (claim.source.artifactId !== claim.artifactId &&
        claim.source.artifactRevisionId !== claim.artifactRevisionId) ||
      "PDF-to-DOCX source and target identities must be distinct",
  ),
  Schema.makeFilter(
    (claim) =>
      claim.artifactRevisionId === claim.output.revisionId ||
      "PDF-to-DOCX output must use the reserved document revision",
  ),
  Schema.makeFilter((claim) => {
    const expected =
      `v1/workspace/workspaces/${claim.lease.workspaceId}` +
      `/projects/${claim.lease.projectId}/artifacts/${claim.artifactId}` +
      `/revisions/${claim.artifactRevisionId}` +
      `/attempts/${claim.lease.leaseGeneration}/output.docx`;
    return (
      claim.output.objectKey === expected ||
      "PDF-to-DOCX output must use the exact generation-scoped document revision key"
    );
  }),
  Schema.makeFilter((claim) => {
    const ownerPrefix =
      claim.source.storage.origin === "artifact-revision"
        ? `/artifacts/${claim.source.artifactId}/revisions/${claim.source.artifactRevisionId}/`
        : `/files/${claim.source.storage.sourceFileId}` +
          `/revisions/${claim.source.storage.sourceFileRevisionId}/`;
    const prefix =
      `v1/${claim.source.retention}/workspaces/${claim.lease.workspaceId}` +
      `/projects/${claim.lease.projectId}${ownerPrefix}`;
    const suffix = claim.source.objectKey.slice(prefix.length);
    const sourceNameMatches =
      claim.source.storage.origin === "project-file-revision"
        ? suffix === "content"
        : suffix.toLowerCase().endsWith(".pdf");
    return (
      (claim.source.objectKey.startsWith(prefix) &&
        suffix !== "" &&
        !suffix.includes("/") &&
        sourceNameMatches) ||
      "PDF-to-DOCX source must use its exact source revision key"
    );
  }),
);

export const OfficeClaimedCreationSchema = Schema.Union([
  OfficeClaimedPromptCreationSchema,
  OfficeClaimedProjectFileImportSchema,
  OfficeClaimedPdfToDocxSchema,
]);

const TerminalCreationAttemptFields = {
  lease: OfficeCreationLeaseSchema,
  terminalStatus: Schema.Literals(["failed", "cancelled"]),
} as const;

export const OfficeTerminalCreationAttemptSchema = Schema.Union([
  Schema.Struct({
    ...TerminalCreationAttemptFields,
    origin: Schema.Literals(["prompt", "project-file-import", "pdf-to-docx"]),
    cleanupRequired: Schema.Literal(false),
  }),
  Schema.Struct({
    ...TerminalCreationAttemptFields,
    origin: Schema.Literals(["prompt", "pdf-to-docx"]),
    cleanupRequired: Schema.Literal(true),
    output: Schema.Struct({ objectKey: ProjectFilePrivateObjectKeySchema }),
  }),
]);

export const OfficeClaimCreationResultSchema = Schema.Union([
  Schema.Struct({ status: Schema.Literal("claimed"), claim: OfficeClaimedCreationSchema }),
  Schema.Struct({
    status: Schema.Literal("terminal"),
    attempt: OfficeTerminalCreationAttemptSchema,
  }),
  Schema.Struct({ status: Schema.Literal("rejected"), error: OfficePublicErrorSchema }),
]);

export const OfficeRenewCreationRequestSchema = Schema.Struct({
  lease: OfficeCreationLeaseSchema,
  leaseDurationMs: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(5_000),
    Schema.isLessThanOrEqualTo(120_000),
  ),
});

export const OfficeRenewCreationResultSchema = Schema.Union([
  Schema.Struct({ status: Schema.Literal("renewed") }),
  Schema.Struct({ status: Schema.Literal("rejected"), error: OfficePublicErrorSchema }),
]);

const PromptCompletionFields = {
  origin: Schema.Literal("prompt"),
  lease: OfficeCreationLeaseSchema,
  output: Schema.Struct({
    objectKey: ProjectFilePrivateObjectKeySchema,
    byteSize: ProjectFilePositiveIntegerSchema.check(
      Schema.isLessThanOrEqualTo(OFFICE_CREATION_MAX_BYTES),
    ),
    sha256: ProjectFileSha256Schema,
    etag: ProjectFileEtagSchema,
  }),
  operationCount: Schema.Int.check(
    Schema.isBetween({ minimum: 1, maximum: OFFICE_CREATION_MAX_ENGINE_OPERATIONS }),
  ),
  identityAliases: Schema.Array(OfficeNativeIdentityAliasSchema).check(Schema.isMaxLength(1_024)),
  compatibility: OfficeCompatibilityReportSchema,
} as const;

const ImportCompletionFields = {
  origin: Schema.Literal("project-file-import"),
  lease: OfficeCreationLeaseSchema,
  engineVersion: OfficeCanonicalIdSchema.check(Schema.isMaxLength(120)),
  compatibility: OfficeImportCompatibilityReportSchema,
} as const;

const ImportCompletionSourceFields = {
  storageContentType: ProjectFileContentTypeSchema,
  byteSize: ProjectFilePositiveIntegerSchema.check(
    Schema.isLessThanOrEqualTo(OFFICE_CREATION_MAX_BYTES),
  ),
  sha256: ProjectFileSha256Schema,
  etag: ProjectFileEtagSchema,
} as const;

const PdfToDocxCompletionSourceFields = {
  artifactId: OfficeArtifactIdSchema,
  artifactRevisionId: OfficeArtifactRevisionIdSchema,
  storageObjectId: OfficeStorageObjectIdSchema,
  contentType: Schema.Literal("application/pdf"),
  storageContentType: OfficePdfToDocxStorageContentTypeSchema,
  byteSize: ProjectFilePositiveIntegerSchema.check(
    Schema.isLessThanOrEqualTo(OFFICE_CREATION_MAX_BYTES),
  ),
  sha256: ProjectFileSha256Schema,
  etag: ProjectFileEtagSchema,
} as const;

export const OfficeCompleteCreationRequestSchema = Schema.Union([
  Schema.Struct({
    ...PromptCompletionFields,
    kind: Schema.Literal("document"),
    engine: Schema.Literal("genoffice-docx"),
    engineVersion: OfficeCanonicalIdSchema.check(Schema.isMaxLength(120)),
    contentType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ),
  }),
  Schema.Struct({
    ...PromptCompletionFields,
    kind: Schema.Literal("presentation"),
    engine: Schema.Literal("genoffice-pptx"),
    engineVersion: OfficeCanonicalIdSchema.check(Schema.isMaxLength(120)),
    contentType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ),
  }),
  Schema.Struct({
    ...PromptCompletionFields,
    kind: Schema.Literal("spreadsheet"),
    engine: Schema.Literal("genoffice-xlsx"),
    engineVersion: OfficeCanonicalIdSchema.check(Schema.isMaxLength(120)),
    contentType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ),
  }),
  Schema.Struct({
    ...ImportCompletionFields,
    kind: Schema.Literal("document"),
    engine: Schema.Literal("genoffice-docx"),
    source: Schema.Struct({
      ...ImportCompletionSourceFields,
      contentType: Schema.Literal(
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ),
    }),
  }),
  Schema.Struct({
    ...ImportCompletionFields,
    kind: Schema.Literal("presentation"),
    engine: Schema.Literal("genoffice-pptx"),
    source: Schema.Struct({
      ...ImportCompletionSourceFields,
      contentType: Schema.Literal(
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      ),
    }),
  }),
  Schema.Struct({
    ...ImportCompletionFields,
    kind: Schema.Literal("spreadsheet"),
    engine: Schema.Literal("genoffice-xlsx"),
    source: Schema.Struct({
      ...ImportCompletionSourceFields,
      contentType: Schema.Literal(
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ),
    }),
  }),
  Schema.Struct({
    ...ImportCompletionFields,
    kind: Schema.Literal("pdf"),
    engine: Schema.Literal("genoffice-pdf"),
    source: Schema.Struct({
      ...ImportCompletionSourceFields,
      contentType: Schema.Literal("application/pdf"),
    }),
  }),
  Schema.Struct({
    ...ImportCompletionFields,
    kind: Schema.Literal("markdown"),
    engine: Schema.Literal("genoffice-markdown"),
    source: Schema.Struct({
      ...ImportCompletionSourceFields,
      contentType: Schema.Literal("text/markdown"),
    }),
  }),
  Schema.Struct({
    origin: Schema.Literal("pdf-to-docx"),
    lease: OfficeCreationLeaseSchema,
    kind: Schema.Literal("document"),
    engine: Schema.Literal("genoffice-docx"),
    engineVersion: OfficeCanonicalIdSchema.check(Schema.isMaxLength(120)),
    source: Schema.Struct(PdfToDocxCompletionSourceFields),
    output: Schema.Struct({
      objectKey: ProjectFilePrivateObjectKeySchema,
      byteSize: ProjectFilePositiveIntegerSchema.check(
        Schema.isLessThanOrEqualTo(OFFICE_CREATION_MAX_BYTES),
      ),
      sha256: ProjectFileSha256Schema,
      etag: ProjectFileEtagSchema,
    }),
    operationCount: Schema.Literal(1),
    identityAliases: Schema.Array(OfficeNativeIdentityAliasSchema).check(Schema.isMaxLength(0)),
    compatibility: OfficeCompatibilityReportSchema,
    conversion: OfficePdfToDocxConversionMetadataSchema,
    contentType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ),
  }),
]);

export const OfficeCompleteCreationResultSchema = Schema.Union([
  Schema.Struct({ status: Schema.Literal("ready"), result: OfficeCreatedArtifactReferenceSchema }),
  Schema.Struct({ status: Schema.Literal("rejected"), error: OfficePublicErrorSchema }),
]);

export const OfficeFailCreationRequestSchema = Schema.Struct({
  lease: OfficeCreationLeaseSchema,
  error: OfficePublicErrorSchema,
}).check(
  Schema.makeFilter(
    (request) =>
      !request.error.retryable ||
      "Retryable Office failures must release the processing attempt instead of becoming terminal",
  ),
);
export const OfficeFailCreationResultSchema = Schema.Struct({ status: Schema.Literal("failed") });

export const OfficeReleaseCreationAttemptRequestSchema = Schema.Struct({
  lease: OfficeCreationLeaseSchema,
});
export const OfficeReleaseCreationAttemptResultSchema = Schema.Union([
  Schema.Struct({ status: Schema.Literal("released") }),
  Schema.Struct({
    status: Schema.Literal("terminal"),
    terminalStatus: Schema.Literals(["failed", "cancelled"]),
    cleanupRequired: Schema.Boolean,
  }),
]);

export const OfficeRecoverCreationAttemptRequestSchema = Schema.Struct({
  lease: OfficeCreationLeaseSchema,
});
export const OfficeRecoverCreationAttemptResultSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("processing"),
    leaseExpired: Schema.Boolean,
  }),
  Schema.Struct({ status: Schema.Literal("released") }),
  Schema.Struct({
    status: Schema.Literal("ready"),
    result: OfficeCreatedArtifactReferenceSchema,
  }),
  Schema.Struct({
    status: Schema.Literal("terminal"),
    terminalStatus: Schema.Literals(["failed", "cancelled"]),
    cleanupRequired: Schema.Boolean,
  }),
]);

export const OfficeAuthorizeCreationCleanupRequestSchema = Schema.Struct({
  ...OfficeWorkerCreationScopeFields,
  objectKey: ProjectFilePrivateObjectKeySchema,
});
export const OfficeAuthorizeCreationCleanupResultSchema = Schema.Struct({
  status: Schema.Literal("authorized"),
});
export const OfficeRecordCreationCleanupRequestSchema = Schema.Struct({
  ...OfficeWorkerCreationScopeFields,
  objectKey: ProjectFilePrivateObjectKeySchema,
  outcome: Schema.Literals(["deleted", "missing"]),
});
export const OfficeRecordCreationCleanupResultSchema = Schema.Struct({
  status: Schema.Literal("deleted"),
});

const ProcessorRequestFields = {
  schemaVersion: Schema.Literal(1),
  maxOutputBytes: Schema.Literal(OFFICE_CREATION_MAX_BYTES),
} as const;

export const OfficePdfToDocxProcessorRequestSchema = Schema.Struct({
  ...ProcessorRequestFields,
  origin: Schema.Literal("pdf-to-docx"),
  sourceFileName: Schema.Literal("source.pdf"),
  sourceContentType: Schema.Literal("application/pdf"),
  sourceByteSize: ProjectFilePositiveIntegerSchema.check(
    Schema.isLessThanOrEqualTo(OFFICE_CREATION_MAX_BYTES),
  ),
  sourceSha256: ProjectFileSha256Schema,
  outputFileName: Schema.Literal("output.docx"),
}).check(OfficeCreationRequestByteSizeFilter);

export const OfficeCreationProcessorRequestSchema = Schema.Union([
  Schema.Struct({
    ...ProcessorRequestFields,
    creation: Schema.Struct({
      kind: Schema.Literal("document"),
      spec: OfficeDocumentCreationSpecSchema,
    }),
    outputFileName: Schema.Literal("output.docx"),
  }),
  Schema.Struct({
    ...ProcessorRequestFields,
    creation: Schema.Struct({
      kind: Schema.Literal("presentation"),
      spec: OfficePresentationCreationSpecSchema,
    }),
    outputFileName: Schema.Literal("output.pptx"),
  }),
  Schema.Struct({
    ...ProcessorRequestFields,
    creation: Schema.Struct({
      kind: Schema.Literal("spreadsheet"),
      spec: OfficeWorkbookCreationSpecSchema,
    }),
    outputFileName: Schema.Literal("output.xlsx"),
  }),
  OfficePdfToDocxProcessorRequestSchema,
]).check(OfficeCreationRequestByteSizeFilter);

const ProcessorReadyFields = {
  status: Schema.Literal("ready"),
  engineVersion: OfficeCanonicalIdSchema.check(Schema.isMaxLength(120)),
  byteSize: ProjectFilePositiveIntegerSchema.check(
    Schema.isLessThanOrEqualTo(OFFICE_CREATION_MAX_BYTES),
  ),
  outputSha256: ProjectFileSha256Schema,
  operationCount: Schema.Int.check(
    Schema.isBetween({ minimum: 1, maximum: OFFICE_CREATION_MAX_ENGINE_OPERATIONS }),
  ),
  identityAliases: Schema.Array(OfficeNativeIdentityAliasSchema).check(Schema.isMaxLength(1_024)),
  compatibility: OfficeCompatibilityReportSchema,
} as const;

export const OfficePdfToDocxProcessorReadyResultSchema = Schema.Struct({
  ...ProcessorReadyFields,
  origin: Schema.Literal("pdf-to-docx"),
  kind: Schema.Literal("document"),
  engine: Schema.Literal("genoffice-docx"),
  contentType: Schema.Literal(
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ),
  operationCount: Schema.Literal(1),
  identityAliases: Schema.Array(OfficeNativeIdentityAliasSchema).check(Schema.isMaxLength(0)),
  conversion: OfficePdfToDocxConversionMetadataSchema,
});

export const OfficeCreationProcessorResultSchema = Schema.Union([
  Schema.Struct({
    ...ProcessorReadyFields,
    kind: Schema.Literal("document"),
    engine: Schema.Literal("genoffice-docx"),
    contentType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ),
  }),
  Schema.Struct({
    ...ProcessorReadyFields,
    kind: Schema.Literal("presentation"),
    engine: Schema.Literal("genoffice-pptx"),
    contentType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ),
  }),
  Schema.Struct({
    ...ProcessorReadyFields,
    kind: Schema.Literal("spreadsheet"),
    engine: Schema.Literal("genoffice-xlsx"),
    contentType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ),
  }),
  OfficePdfToDocxProcessorReadyResultSchema,
  Schema.Struct({ status: Schema.Literal("failed"), error: OfficePublicErrorSchema }),
]);

const ImportProcessorRequestFields = {
  schemaVersion: Schema.Literal(1),
  sourceByteSize: ProjectFilePositiveIntegerSchema.check(
    Schema.isLessThanOrEqualTo(OFFICE_CREATION_MAX_BYTES),
  ),
  sourceSha256: ProjectFileSha256Schema,
} as const;

export const OfficeImportProcessorRequestSchema = Schema.Union([
  Schema.Struct({
    ...ImportProcessorRequestFields,
    kind: Schema.Literal("document"),
    sourceFileName: Schema.Literal("source.docx"),
  }),
  Schema.Struct({
    ...ImportProcessorRequestFields,
    kind: Schema.Literal("presentation"),
    sourceFileName: Schema.Literal("source.pptx"),
  }),
  Schema.Struct({
    ...ImportProcessorRequestFields,
    kind: Schema.Literal("spreadsheet"),
    sourceFileName: Schema.Literal("source.xlsx"),
  }),
  Schema.Struct({
    ...ImportProcessorRequestFields,
    kind: Schema.Literal("pdf"),
    sourceFileName: Schema.Literal("source.pdf"),
  }),
  Schema.Struct({
    ...ImportProcessorRequestFields,
    kind: Schema.Literal("markdown"),
    sourceFileName: Schema.Literal("source.md"),
  }),
]);

const ImportProcessorReadyFields = {
  status: Schema.Literal("ready"),
  engineVersion: OfficeCanonicalIdSchema.check(Schema.isMaxLength(120)),
  byteSize: ProjectFilePositiveIntegerSchema.check(
    Schema.isLessThanOrEqualTo(OFFICE_CREATION_MAX_BYTES),
  ),
  compatibility: OfficeImportCompatibilityReportSchema,
} as const;

export const OfficeImportProcessorResultSchema = Schema.Union([
  Schema.Struct({
    ...ImportProcessorReadyFields,
    kind: Schema.Literal("document"),
    engine: Schema.Literal("genoffice-docx"),
    contentType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ),
  }),
  Schema.Struct({
    ...ImportProcessorReadyFields,
    kind: Schema.Literal("presentation"),
    engine: Schema.Literal("genoffice-pptx"),
    contentType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ),
  }),
  Schema.Struct({
    ...ImportProcessorReadyFields,
    kind: Schema.Literal("spreadsheet"),
    engine: Schema.Literal("genoffice-xlsx"),
    contentType: Schema.Literal(
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ),
  }),
  Schema.Struct({
    ...ImportProcessorReadyFields,
    kind: Schema.Literal("pdf"),
    engine: Schema.Literal("genoffice-pdf"),
    contentType: Schema.Literal("application/pdf"),
  }),
  Schema.Struct({
    ...ImportProcessorReadyFields,
    kind: Schema.Literal("markdown"),
    engine: Schema.Literal("genoffice-markdown"),
    contentType: Schema.Literal("text/markdown"),
  }),
  Schema.Struct({ status: Schema.Literal("failed"), error: OfficePublicErrorSchema }),
]);

export const OfficeDocumentCreationSpecStandardSchema = toPapersFlowStandardSchema(
  OfficeDocumentCreationSpecSchema,
);
export const OfficePresentationCreationSpecStandardSchema = toPapersFlowStandardSchema(
  OfficePresentationCreationSpecSchema,
);
export const OfficeWorkbookCreationSpecStandardSchema = toPapersFlowStandardSchema(
  OfficeWorkbookCreationSpecSchema,
);
export const OfficeCreatedArtifactReferenceStandardSchema = toPapersFlowStandardSchema(
  OfficeCreatedArtifactReferenceSchema,
);

export type OfficeDocumentCreationBlock = typeof OfficeDocumentCreationBlockSchema.Type;
export type OfficeDocumentCreationSpec = typeof OfficeDocumentCreationSpecSchema.Type;
export type OfficePresentationCreationSlide = typeof OfficePresentationCreationSlideSchema.Type;
export type OfficePresentationCreationSpec = typeof OfficePresentationCreationSpecSchema.Type;
export type OfficeWorkbookCell = typeof OfficeWorkbookCellSchema.Type;
export type OfficeWorkbookColumnFormat = typeof OfficeWorkbookColumnFormatSchema.Type;
export type OfficeWorkbookCreationSheet = typeof OfficeWorkbookCreationSheetSchema.Type;
export type OfficeWorkbookCreationSpec = typeof OfficeWorkbookCreationSpecSchema.Type;
export type OfficeCreationSpec = typeof OfficeCreationSpecSchema.Type;
export type OfficeCreationSpecEncoded = typeof OfficeCreationSpecSchema.Encoded;
export type OfficeCompatibilityIssue = typeof OfficeCompatibilityIssueSchema.Type;
export type OfficeCompatibilityReport = typeof OfficeCompatibilityReportSchema.Type;
export type OfficeImportCompatibilityIssue = typeof OfficeImportCompatibilityIssueSchema.Type;
export type OfficeImportCompatibilityReport = typeof OfficeImportCompatibilityReportSchema.Type;
export type OfficeImportCompatibilityReportEncoded =
  typeof OfficeImportCompatibilityReportSchema.Encoded;
export type OfficePdfToDocxConversionMetadata = typeof OfficePdfToDocxConversionMetadataSchema.Type;
export type OfficePdfToDocxConversionMetadataEncoded =
  typeof OfficePdfToDocxConversionMetadataSchema.Encoded;
export type OfficePromptCreatedArtifactReference =
  typeof OfficePromptCreatedArtifactReferenceSchema.Type;
export type OfficeImportedArtifactReference = typeof OfficeImportedArtifactReferenceSchema.Type;
export type OfficeImportedArtifactReferenceEncoded =
  typeof OfficeImportedArtifactReferenceSchema.Encoded;
export type OfficePdfToDocxArtifactReference = typeof OfficePdfToDocxArtifactReferenceSchema.Type;
export type OfficePdfToDocxArtifactReferenceEncoded =
  typeof OfficePdfToDocxArtifactReferenceSchema.Encoded;
export type OfficeCreatedArtifactReference = typeof OfficeCreatedArtifactReferenceSchema.Type;
export type OfficeCreatedArtifactReferenceEncoded =
  typeof OfficeCreatedArtifactReferenceSchema.Encoded;
export type OfficeProjectFilePromotionRequest = typeof OfficeProjectFilePromotionRequestSchema.Type;
export type OfficeProjectFilePromotionRequestEncoded =
  typeof OfficeProjectFilePromotionRequestSchema.Encoded;
export type OfficePdfToDocxRequest = typeof OfficePdfToDocxRequestSchema.Type;
export type OfficePdfToDocxRequestEncoded = typeof OfficePdfToDocxRequestSchema.Encoded;
export type OfficeRepositoryBeginCreationRequest =
  typeof OfficeRepositoryBeginCreationRequestSchema.Type;
export type OfficeRepositoryBeginCreationResult =
  typeof OfficeRepositoryBeginCreationResultSchema.Type;
export type OfficeRepositoryBeginProjectFileImportRequest =
  typeof OfficeRepositoryBeginProjectFileImportRequestSchema.Type;
export type OfficeRepositoryBeginProjectFileImportRequestEncoded =
  typeof OfficeRepositoryBeginProjectFileImportRequestSchema.Encoded;
export type OfficeRepositoryBeginProjectFileImportResult =
  typeof OfficeRepositoryBeginProjectFileImportResultSchema.Type;
export type OfficeRepositoryBeginProjectFileImportResultEncoded =
  typeof OfficeRepositoryBeginProjectFileImportResultSchema.Encoded;
export type OfficeRepositoryBeginPdfToDocxRequest =
  typeof OfficeRepositoryBeginPdfToDocxRequestSchema.Type;
export type OfficeRepositoryBeginPdfToDocxRequestEncoded =
  typeof OfficeRepositoryBeginPdfToDocxRequestSchema.Encoded;
export type OfficeRepositoryBeginPdfToDocxResult =
  typeof OfficeRepositoryBeginPdfToDocxResultSchema.Type;
export type OfficeRepositoryBeginPdfToDocxResultEncoded =
  typeof OfficeRepositoryBeginPdfToDocxResultSchema.Encoded;
export type OfficeRepositoryGetCreationRequest =
  typeof OfficeRepositoryGetCreationRequestSchema.Type;
export type OfficeRepositoryGetProjectFileImportRequest =
  typeof OfficeRepositoryGetProjectFileImportRequestSchema.Type;
export type OfficeRepositoryGetProjectFileImportRequestEncoded =
  typeof OfficeRepositoryGetProjectFileImportRequestSchema.Encoded;
export type OfficeRepositoryCancelProjectFileImportRequest =
  typeof OfficeRepositoryCancelProjectFileImportRequestSchema.Type;
export type OfficeRepositoryCancelProjectFileImportRequestEncoded =
  typeof OfficeRepositoryCancelProjectFileImportRequestSchema.Encoded;
export type OfficeRepositoryGetPdfToDocxRequest =
  typeof OfficeRepositoryGetPdfToDocxRequestSchema.Type;
export type OfficeRepositoryGetPdfToDocxRequestEncoded =
  typeof OfficeRepositoryGetPdfToDocxRequestSchema.Encoded;
export type OfficeRepositoryCancelPdfToDocxRequest =
  typeof OfficeRepositoryCancelPdfToDocxRequestSchema.Type;
export type OfficeRepositoryCancelPdfToDocxRequestEncoded =
  typeof OfficeRepositoryCancelPdfToDocxRequestSchema.Encoded;
export type OfficeRepositoryCancelCreationRequest =
  typeof OfficeRepositoryCancelCreationRequestSchema.Type;
export type OfficeRepositoryCreationStatus = typeof OfficeRepositoryCreationStatusSchema.Type;
export type OfficeRepositoryProjectFileImportStatus =
  typeof OfficeRepositoryProjectFileImportStatusSchema.Type;
export type OfficeRepositoryProjectFileImportStatusEncoded =
  typeof OfficeRepositoryProjectFileImportStatusSchema.Encoded;
export type OfficeRepositoryPdfToDocxStatus = typeof OfficeRepositoryPdfToDocxStatusSchema.Type;
export type OfficeRepositoryPdfToDocxStatusEncoded =
  typeof OfficeRepositoryPdfToDocxStatusSchema.Encoded;
export type OfficeRepositoryCancelCreationResult =
  typeof OfficeRepositoryCancelCreationResultSchema.Type;
export type OfficeRepositoryCancelProjectFileImportResult =
  typeof OfficeRepositoryCancelProjectFileImportResultSchema.Type;
export type OfficeRepositoryCancelProjectFileImportResultEncoded =
  typeof OfficeRepositoryCancelProjectFileImportResultSchema.Encoded;
export type OfficeRepositoryCancelPdfToDocxResult =
  typeof OfficeRepositoryCancelPdfToDocxResultSchema.Type;
export type OfficeRepositoryCancelPdfToDocxResultEncoded =
  typeof OfficeRepositoryCancelPdfToDocxResultSchema.Encoded;
export type OfficeCreationPointer = typeof OfficeCreationPointerSchema.Type;
export type OfficeCreationPointerEncoded = typeof OfficeCreationPointerSchema.Encoded;
export type OfficeCreationLease = typeof OfficeCreationLeaseSchema.Type;
export type OfficeProjectFileImportSource = typeof OfficeProjectFileImportSourceSchema.Type;
export type OfficeProjectFileImportSourceEncoded =
  typeof OfficeProjectFileImportSourceSchema.Encoded;
export type OfficeClaimedPromptCreation = typeof OfficeClaimedPromptCreationSchema.Type;
export type OfficeClaimedProjectFileImport = typeof OfficeClaimedProjectFileImportSchema.Type;
export type OfficePdfToDocxSourceStorage = typeof OfficePdfToDocxSourceStorageSchema.Type;
export type OfficePdfToDocxStorageContentType = typeof OfficePdfToDocxStorageContentTypeSchema.Type;
export type OfficePdfToDocxSource = typeof OfficePdfToDocxSourceSchema.Type;
export type OfficeClaimedPdfToDocx = typeof OfficeClaimedPdfToDocxSchema.Type;
export type OfficeClaimedCreation = typeof OfficeClaimedCreationSchema.Type;
export type OfficeClaimedCreationEncoded = typeof OfficeClaimedCreationSchema.Encoded;
export type OfficeTerminalCreationAttempt = typeof OfficeTerminalCreationAttemptSchema.Type;
export type OfficeClaimCreationResult = typeof OfficeClaimCreationResultSchema.Type;
export type OfficeCompleteCreationRequest = typeof OfficeCompleteCreationRequestSchema.Type;
export type OfficeReleaseCreationAttemptRequest =
  typeof OfficeReleaseCreationAttemptRequestSchema.Type;
export type OfficeReleaseCreationAttemptResult =
  typeof OfficeReleaseCreationAttemptResultSchema.Type;
export type OfficeRecoverCreationAttemptRequest =
  typeof OfficeRecoverCreationAttemptRequestSchema.Type;
export type OfficeRecoverCreationAttemptResult =
  typeof OfficeRecoverCreationAttemptResultSchema.Type;
export type OfficeCreationProcessorRequest = typeof OfficeCreationProcessorRequestSchema.Type;
export type OfficeCreationProcessorResult = typeof OfficeCreationProcessorResultSchema.Type;
export type OfficePdfToDocxProcessorRequest = typeof OfficePdfToDocxProcessorRequestSchema.Type;
export type OfficePdfToDocxProcessorRequestEncoded =
  typeof OfficePdfToDocxProcessorRequestSchema.Encoded;
export type OfficePdfToDocxProcessorReadyResult =
  typeof OfficePdfToDocxProcessorReadyResultSchema.Type;
export type OfficePdfToDocxProcessorReadyResultEncoded =
  typeof OfficePdfToDocxProcessorReadyResultSchema.Encoded;
export type OfficeImportProcessorRequest = typeof OfficeImportProcessorRequestSchema.Type;
export type OfficeImportProcessorRequestEncoded = typeof OfficeImportProcessorRequestSchema.Encoded;
export type OfficeImportProcessorResult = typeof OfficeImportProcessorResultSchema.Type;
export type OfficeImportProcessorResultEncoded = typeof OfficeImportProcessorResultSchema.Encoded;

const decode = <A, I>(schema: Schema.Codec<A, I>) =>
  Schema.decodeUnknownSync(schema, OfficeBoundaryParseOptions);

export const decodeOfficeDocumentCreationSpec = decode(OfficeDocumentCreationSpecSchema);
export const decodeOfficePresentationCreationSpec = decode(OfficePresentationCreationSpecSchema);
export const decodeOfficeWorkbookCreationSpec = decode(OfficeWorkbookCreationSpecSchema);
export const decodeOfficeCreationSpec = decode(OfficeCreationSpecSchema);
export const decodeOfficeCompatibilityReport = decode(OfficeCompatibilityReportSchema);
export const decodeOfficeImportCompatibilityReport = decode(OfficeImportCompatibilityReportSchema);
export const decodeOfficePdfToDocxConversionMetadata = decode(
  OfficePdfToDocxConversionMetadataSchema,
);
export const decodeOfficeCreatedArtifactReference = decode(OfficeCreatedArtifactReferenceSchema);
export const decodeOfficeProjectFilePromotionRequest = decode(
  OfficeProjectFilePromotionRequestSchema,
);
export const decodeOfficePdfToDocxRequest = decode(OfficePdfToDocxRequestSchema);
export const decodeOfficeRepositoryBeginCreationRequest = decode(
  OfficeRepositoryBeginCreationRequestSchema,
);
export const decodeOfficeRepositoryBeginCreationResult = decode(
  OfficeRepositoryBeginCreationResultSchema,
);
export const decodeOfficeRepositoryBeginProjectFileImportRequest = decode(
  OfficeRepositoryBeginProjectFileImportRequestSchema,
);
export const decodeOfficeRepositoryBeginProjectFileImportResult = decode(
  OfficeRepositoryBeginProjectFileImportResultSchema,
);
export const decodeOfficeRepositoryBeginPdfToDocxRequest = decode(
  OfficeRepositoryBeginPdfToDocxRequestSchema,
);
export const decodeOfficeRepositoryBeginPdfToDocxResult = decode(
  OfficeRepositoryBeginPdfToDocxResultSchema,
);
export const decodeOfficeRepositoryGetCreationRequest = decode(
  OfficeRepositoryGetCreationRequestSchema,
);
export const decodeOfficeRepositoryGetProjectFileImportRequest = decode(
  OfficeRepositoryGetProjectFileImportRequestSchema,
);
export const decodeOfficeRepositoryCancelProjectFileImportRequest = decode(
  OfficeRepositoryCancelProjectFileImportRequestSchema,
);
export const decodeOfficeRepositoryGetPdfToDocxRequest = decode(
  OfficeRepositoryGetPdfToDocxRequestSchema,
);
export const decodeOfficeRepositoryCancelPdfToDocxRequest = decode(
  OfficeRepositoryCancelPdfToDocxRequestSchema,
);
export const decodeOfficeRepositoryCancelCreationRequest = decode(
  OfficeRepositoryCancelCreationRequestSchema,
);
export const decodeOfficeRepositoryCreationStatus = decode(OfficeRepositoryCreationStatusSchema);
export const decodeOfficeRepositoryProjectFileImportStatus = decode(
  OfficeRepositoryProjectFileImportStatusSchema,
);
export const decodeOfficeRepositoryPdfToDocxStatus = decode(OfficeRepositoryPdfToDocxStatusSchema);
export const decodeOfficeRepositoryCancelCreationResult = decode(
  OfficeRepositoryCancelCreationResultSchema,
);
export const decodeOfficeRepositoryCancelProjectFileImportResult = decode(
  OfficeRepositoryCancelProjectFileImportResultSchema,
);
export const decodeOfficeRepositoryCancelPdfToDocxResult = decode(
  OfficeRepositoryCancelPdfToDocxResultSchema,
);
export const decodeOfficeCreationPointer = decode(OfficeCreationPointerSchema);
export const decodeOfficeCreationStartResult = decode(OfficeCreationStartResultSchema);
export const decodeOfficeClaimCreationRequest = decode(OfficeClaimCreationRequestSchema);
export const decodeOfficeProjectFileImportSource = decode(OfficeProjectFileImportSourceSchema);
export const decodeOfficePdfToDocxSource = decode(OfficePdfToDocxSourceSchema);
export const decodeOfficeClaimedCreation = decode(OfficeClaimedCreationSchema);
export const decodeOfficeClaimCreationResult = decode(OfficeClaimCreationResultSchema);
export const decodeOfficeRenewCreationRequest = decode(OfficeRenewCreationRequestSchema);
export const decodeOfficeRenewCreationResult = decode(OfficeRenewCreationResultSchema);
export const decodeOfficeCompleteCreationRequest = decode(OfficeCompleteCreationRequestSchema);
export const decodeOfficeCompleteCreationResult = decode(OfficeCompleteCreationResultSchema);
export const decodeOfficeFailCreationRequest = decode(OfficeFailCreationRequestSchema);
export const decodeOfficeFailCreationResult = decode(OfficeFailCreationResultSchema);
export const decodeOfficeReleaseCreationAttemptRequest = decode(
  OfficeReleaseCreationAttemptRequestSchema,
);
export const decodeOfficeReleaseCreationAttemptResult = decode(
  OfficeReleaseCreationAttemptResultSchema,
);
export const decodeOfficeRecoverCreationAttemptRequest = decode(
  OfficeRecoverCreationAttemptRequestSchema,
);
export const decodeOfficeRecoverCreationAttemptResult = decode(
  OfficeRecoverCreationAttemptResultSchema,
);
export const decodeOfficeAuthorizeCreationCleanupRequest = decode(
  OfficeAuthorizeCreationCleanupRequestSchema,
);
export const decodeOfficeAuthorizeCreationCleanupResult = decode(
  OfficeAuthorizeCreationCleanupResultSchema,
);
export const decodeOfficeRecordCreationCleanupRequest = decode(
  OfficeRecordCreationCleanupRequestSchema,
);
export const decodeOfficeRecordCreationCleanupResult = decode(
  OfficeRecordCreationCleanupResultSchema,
);
export const decodeOfficeCreationProcessorRequest = decode(OfficeCreationProcessorRequestSchema);
export const decodeOfficeCreationProcessorResult = decode(OfficeCreationProcessorResultSchema);
export const decodeOfficePdfToDocxProcessorRequest = decode(OfficePdfToDocxProcessorRequestSchema);
export const decodeOfficePdfToDocxProcessorReadyResult = decode(
  OfficePdfToDocxProcessorReadyResultSchema,
);
export const decodeOfficeImportProcessorRequest = decode(OfficeImportProcessorRequestSchema);
export const decodeOfficeImportProcessorResult = decode(OfficeImportProcessorResultSchema);

export const encodeOfficeCreationSpec = Schema.encodeSync(OfficeCreationSpecSchema);
export const encodeOfficePdfToDocxConversionMetadata = Schema.encodeSync(
  OfficePdfToDocxConversionMetadataSchema,
);
export const encodeOfficeCreatedArtifactReference = Schema.encodeSync(
  OfficeCreatedArtifactReferenceSchema,
);
export const encodeOfficeProjectFilePromotionRequest = Schema.encodeSync(
  OfficeProjectFilePromotionRequestSchema,
);
export const encodeOfficePdfToDocxRequest = Schema.encodeSync(OfficePdfToDocxRequestSchema);
export const encodeOfficeRepositoryBeginCreationRequest = Schema.encodeSync(
  OfficeRepositoryBeginCreationRequestSchema,
);
export const encodeOfficeRepositoryBeginProjectFileImportRequest = Schema.encodeSync(
  OfficeRepositoryBeginProjectFileImportRequestSchema,
);
export const encodeOfficeRepositoryBeginProjectFileImportResult = Schema.encodeSync(
  OfficeRepositoryBeginProjectFileImportResultSchema,
);
export const encodeOfficeRepositoryBeginPdfToDocxRequest = Schema.encodeSync(
  OfficeRepositoryBeginPdfToDocxRequestSchema,
);
export const encodeOfficeRepositoryBeginPdfToDocxResult = Schema.encodeSync(
  OfficeRepositoryBeginPdfToDocxResultSchema,
);
export const encodeOfficeRepositoryGetCreationRequest = Schema.encodeSync(
  OfficeRepositoryGetCreationRequestSchema,
);
export const encodeOfficeRepositoryGetProjectFileImportRequest = Schema.encodeSync(
  OfficeRepositoryGetProjectFileImportRequestSchema,
);
export const encodeOfficeRepositoryCancelProjectFileImportRequest = Schema.encodeSync(
  OfficeRepositoryCancelProjectFileImportRequestSchema,
);
export const encodeOfficeRepositoryGetPdfToDocxRequest = Schema.encodeSync(
  OfficeRepositoryGetPdfToDocxRequestSchema,
);
export const encodeOfficeRepositoryCancelPdfToDocxRequest = Schema.encodeSync(
  OfficeRepositoryCancelPdfToDocxRequestSchema,
);
export const encodeOfficeRepositoryProjectFileImportStatus = Schema.encodeSync(
  OfficeRepositoryProjectFileImportStatusSchema,
);
export const encodeOfficeRepositoryPdfToDocxStatus = Schema.encodeSync(
  OfficeRepositoryPdfToDocxStatusSchema,
);
export const encodeOfficeRepositoryCancelProjectFileImportResult = Schema.encodeSync(
  OfficeRepositoryCancelProjectFileImportResultSchema,
);
export const encodeOfficeRepositoryCancelPdfToDocxResult = Schema.encodeSync(
  OfficeRepositoryCancelPdfToDocxResultSchema,
);
export const encodeOfficeRepositoryCancelCreationRequest = Schema.encodeSync(
  OfficeRepositoryCancelCreationRequestSchema,
);
export const encodeOfficeCreationPointer = Schema.encodeSync(OfficeCreationPointerSchema);
export const encodeOfficeClaimCreationRequest = Schema.encodeSync(OfficeClaimCreationRequestSchema);
export const encodeOfficeProjectFileImportSource = Schema.encodeSync(
  OfficeProjectFileImportSourceSchema,
);
export const encodeOfficePdfToDocxSource = Schema.encodeSync(OfficePdfToDocxSourceSchema);
export const encodeOfficeClaimedCreation = Schema.encodeSync(OfficeClaimedCreationSchema);
export const encodeOfficeRenewCreationRequest = Schema.encodeSync(OfficeRenewCreationRequestSchema);
export const encodeOfficeCompleteCreationRequest = Schema.encodeSync(
  OfficeCompleteCreationRequestSchema,
);
export const encodeOfficeFailCreationRequest = Schema.encodeSync(OfficeFailCreationRequestSchema);
export const encodeOfficeReleaseCreationAttemptRequest = Schema.encodeSync(
  OfficeReleaseCreationAttemptRequestSchema,
);
export const encodeOfficeRecoverCreationAttemptRequest = Schema.encodeSync(
  OfficeRecoverCreationAttemptRequestSchema,
);
export const encodeOfficeAuthorizeCreationCleanupRequest = Schema.encodeSync(
  OfficeAuthorizeCreationCleanupRequestSchema,
);
export const encodeOfficeRecordCreationCleanupRequest = Schema.encodeSync(
  OfficeRecordCreationCleanupRequestSchema,
);
export const encodeOfficeCreationProcessorRequest = Schema.encodeSync(
  OfficeCreationProcessorRequestSchema,
);
export const encodeOfficeCreationProcessorResult = Schema.encodeSync(
  OfficeCreationProcessorResultSchema,
);
export const encodeOfficePdfToDocxProcessorRequest = Schema.encodeSync(
  OfficePdfToDocxProcessorRequestSchema,
);
export const encodeOfficePdfToDocxProcessorReadyResult = Schema.encodeSync(
  OfficePdfToDocxProcessorReadyResultSchema,
);
export const encodeOfficeImportProcessorRequest = Schema.encodeSync(
  OfficeImportProcessorRequestSchema,
);
export const encodeOfficeImportProcessorResult = Schema.encodeSync(
  OfficeImportProcessorResultSchema,
);
